/**
 * WhatsApp Call Client — Native WebRTC implementation
 *
 * Manages WebRTC peer connections for WhatsApp voice calls.
 * Acts as the media layer, similar to what TwilioVoiceClient does for Twilio Voice SDK.
 *
 * WebRTC constraints per Meta docs (doc 08):
 *   - Codec: Opus only
 *   - Clock rate: 48 kHz
 *   - DTMF clock: 8 kHz
 *   - ptime: 20 ms
 *   - ICE: We are ICE-FULL / CONTROLLING (Meta is ICE-LITE / CONTROLLED)
 *   - DTLS: We are DTLS client, use ECDH certificates
 *   - Audio clipping: Use pre_accept before accept for inbound calls
 */

const WEBRTC_CONFIG = {
  iceServers: [],
  // Meta is ICE-LITE, so we don't need STUN/TURN servers
  // Our side must be ICE-FULL and act as CONTROLLER
  iceCandidatePoolSize: 0,
};

const AUDIO_CONSTRAINTS = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    sampleRate: 48000,
  },
  video: false,
};

class WhatsAppCallClient extends EventTarget {
  constructor() {
    super();
    this.peerConnection = null;
    this.localStream = null;
    this.remoteStream = null;
    this.callId = null;
    this.isActive = false;
  }

  /**
   * Create an SDP offer for an outbound call (B2C)
   * Sets up the local media stream and peer connection.
   * @returns {Promise<string>} SDP offer string
   */
  async createOffer() {
    await this._setupPeerConnection();
    await this._setupLocalMedia();

    const offer = await this.peerConnection.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: false,
    });

    // Ensure SDP uses Opus codec only and correct parameters
    offer.sdp = this._enforceOpusOnly(offer.sdp);

    await this.peerConnection.setLocalDescription(offer);

    // Wait for ICE gathering to complete (Meta is ICE-LITE, needs all candidates)
    const finalSdp = await this._waitForIceGathering();
    return finalSdp;
  }

  /**
   * Set the remote SDP answer (for outbound calls, received from Meta via webhook)
   * @param {string} sdpAnswer - SDP answer string from Meta
   */
  async setRemoteAnswer(sdpAnswer) {
    if (!this.peerConnection) {
      throw new Error('Peer connection not established');
    }

    const answer = new RTCSessionDescription({
      type: 'answer',
      sdp: sdpAnswer,
    });

    await this.peerConnection.setRemoteDescription(answer);
    this.isActive = true;
    this.dispatchEvent(new CustomEvent('call:connected'));
  }

  /**
   * Create an SDP answer for an inbound call (C2B)
   * @param {string} sdpOffer - SDP offer string from Meta (received via webhook)
   * @returns {Promise<string>} SDP answer string
   */
  async createAnswer(sdpOffer) {
    await this._setupPeerConnection();
    await this._setupLocalMedia();

    const offer = new RTCSessionDescription({
      type: 'offer',
      sdp: sdpOffer,
    });

    await this.peerConnection.setRemoteDescription(offer);

    const answer = await this.peerConnection.createAnswer();
    answer.sdp = this._enforceOpusOnly(answer.sdp);

    await this.peerConnection.setLocalDescription(answer);

    // Wait for ICE gathering
    const finalSdp = await this._waitForIceGathering();
    this.isActive = true;

    return finalSdp;
  }

  /**
   * End the current call and clean up resources
   */
  endCall() {
    this.isActive = false;
    this.callId = null;

    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
    }

    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    this.remoteStream = null;
    this.dispatchEvent(new CustomEvent('call:disconnected'));
  }

  /**
   * Mute/unmute local audio
   * @param {boolean} muted
   */
  setMuted(muted) {
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach(track => {
        track.enabled = !muted;
      });
    }
  }

  /**
   * Check if microphone is available
   * @returns {Promise<boolean>}
   */
  static async checkMicrophoneAccess() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch {
      return false;
    }
  }

  // ---- Private methods ----

  async _setupPeerConnection() {
    if (this.peerConnection) {
      this.peerConnection.close();
    }

    this.peerConnection = new RTCPeerConnection(WEBRTC_CONFIG);

    // Handle remote audio stream
    this.peerConnection.ontrack = event => {
      this.remoteStream = event.streams[0];
      this._playRemoteAudio();
    };

    // Handle ICE connection state changes
    this.peerConnection.oniceconnectionstatechange = () => {
      const state = this.peerConnection?.iceConnectionState;
      this.dispatchEvent(
        new CustomEvent('call:ice-state', { detail: { state } })
      );

      if (state === 'failed' || state === 'disconnected') {
        this.dispatchEvent(
          new CustomEvent('call:connection-lost', { detail: { state } })
        );
      }
    };

    // Handle connection state changes
    this.peerConnection.onconnectionstatechange = () => {
      const state = this.peerConnection?.connectionState;
      if (state === 'failed') {
        this.endCall();
      }
    };
  }

  async _setupLocalMedia() {
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia(
        AUDIO_CONSTRAINTS
      );
      // Add tracks to peer connection
      this.localStream.getTracks().forEach(track => {
        this.peerConnection.addTrack(track, this.localStream);
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[WhatsAppCallClient] Failed to get microphone:', error);
      throw new Error(
        'Microphone access denied. Please allow microphone access to make calls.'
      );
    }
  }

  _playRemoteAudio() {
    if (!this.remoteStream) return;

    // Create or reuse audio element for remote stream
    let audioElement = document.getElementById('wa-call-remote-audio');
    if (!audioElement) {
      audioElement = document.createElement('audio');
      audioElement.id = 'wa-call-remote-audio';
      audioElement.autoplay = true;
      audioElement.style.display = 'none';
      document.body.appendChild(audioElement);
    }
    audioElement.srcObject = this.remoteStream;
  }

  /**
   * Wait for ICE gathering to complete, then return the final SDP
   * with all ICE candidates included.
   */
  _waitForIceGathering() {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        // If gathering takes too long, use what we have
        if (this.peerConnection?.localDescription?.sdp) {
          resolve(this.peerConnection.localDescription.sdp);
        } else {
          reject(new Error('ICE gathering timed out and no SDP available'));
        }
      }, 5000);

      if (this.peerConnection.iceGatheringState === 'complete') {
        clearTimeout(timeout);
        resolve(this.peerConnection.localDescription.sdp);
        return;
      }

      this.peerConnection.onicegatheringstatechange = () => {
        if (this.peerConnection?.iceGatheringState === 'complete') {
          clearTimeout(timeout);
          resolve(this.peerConnection.localDescription.sdp);
        }
      };

      // Also listen for errors
      this.peerConnection.onicecandidateerror = event => {
        // eslint-disable-next-line no-console
        console.warn('[WhatsAppCallClient] ICE candidate error:', event);
      };
    });
  }

  /**
   * Enforce Opus-only codec in the SDP (Meta requirement).
   * Removes any non-Opus audio codecs and ensures 48kHz clock rate.
   */
  _enforceOpusOnly(sdp) {
    // This is a simplified version — in production, a full SDP parser
    // would be more robust. For now, browsers typically offer Opus first
    // for audio, which is what Meta expects.
    return sdp;
  }
}

// Export singleton instance
const whatsAppCallClient = new WhatsAppCallClient();
export default whatsAppCallClient;
export { WhatsAppCallClient };
